/**
 * RadioCard — carte cliquable type radio (1 sur N) ou case (multi).
 *
 *   <RadioCard
 *     selected={mode === 'open'}
 *     onSelect={() => setMode('open')}
 *     icon={<Circle size={16} />}
 *     label="Open edition"
 *     description="Sans limite"
 *   />
 *
 * Pattern « Direction C — fiche conservateur » : utilisé dans le modal
 * d'ajout de variante pour Type d'édition (4 cartes 1-sur-N), Format
 * (grille de cartes avec dimensions/ratio à droite) et tout autre choix
 * éditorial qui mérite plus qu'un select natif.
 *
 * Pas de saisie texte ici — c'est un toggle visuel. La variante `multi`
 * change uniquement l'aria role (`checkbox` au lieu de `radio`) ; le
 * comportement reste « onSelect = toggle ».
 */
import type { ReactNode } from 'react'

import './RadioCard.css'

interface RadioCardProps {
  selected: boolean
  onSelect: () => void
  icon?: ReactNode
  label: string
  description?: string
  meta?: ReactNode
  disabled?: boolean
  multi?: boolean
  className?: string
}

export default function RadioCard({
  selected,
  onSelect,
  icon,
  label,
  description,
  meta,
  disabled,
  multi,
  className,
}: RadioCardProps) {
  const classes = [
    'radio-card',
    selected && 'radio-card--selected',
    disabled && 'radio-card--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      className={classes}
      onClick={() => !disabled && onSelect()}
    >
      {icon && (
        <span className="radio-card-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="radio-card-body">
        <span className="radio-card-label">{label}</span>
        {description && <span className="radio-card-description">{description}</span>}
      </span>
      {meta && <span className="radio-card-meta">{meta}</span>}
    </button>
  )
}
